import { Button, IconButton, TextButton } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { Input } from '@creditornot/cb-input';
import { ModalHeader } from '@creditornot/cb-modal';
import { Tabs } from '@creditornot/cb-tabs';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import GirlShrugging from 'common/illustrations/GirlShrugging';
import { Typography as T } from 'common/typography';

import { VenueRow } from './VenueRow';
import { filteredVenues } from './helpers';
import {
  Buttons,
  LeftPanel,
  HeaderContent,
  SearchContainer,
  StyledModalContent,
  VenueListContainer,
  Error,
  EmptyResults,
  StyledFooter,
  NumberSearchResults,
  EmptyImageWrapper,
} from './styles';

export type FormValues = {
  venueIds: string[];
};

type Props = {
  venues: Venue[];
  onClose: () => void;
  onBackClick?: () => void;
  displayCancelButton?: boolean;
  disableSaveButtonByDefault?: boolean;
};

type TabId = 'all-venues' | 'selected-venues';

export const AddVenuesToVenueGroup = ({
  venues,
  onClose,
  onBackClick,
  displayCancelButton = true,
  disableSaveButtonByDefault = false,
}: Props) => {
  const intl = useIntl();
  const tabMessage = (id: string, count: number) => intl.formatMessage({ id }, { count });
  const [activeTab, setActiveTab] = useState<TabId>('all-venues');

  const { change, getState } = useForm<FormValues>();
  const {
    submitting,
    submitError,
    initialValues,
    values: { venueIds = [] },
  } = getState();

  const isDisableSaveButton = useMemo(() => {
    const initialVenueIds = (initialValues && initialValues.venueIds) || [];

    const hasSameValues =
      initialVenueIds.length === venueIds.length &&
      initialVenueIds.every((venueId: string) => venueIds.includes(venueId));

    return submitting || hasSameValues;
  }, [initialValues, venueIds, submitting]);

  const [searchKeyword, setSearchKeyword] = useState('');

  const totalVenues = venues?.length ?? 0;

  const matchingVenues = useMemo(() => filteredVenues(venues, searchKeyword), [searchKeyword, venues]);

  const selectedVenuesValues = useMemo(() => venues.filter(venue => venueIds.includes(venue.id)), [venues, venueIds]);

  const matchingSelectedVenues = useMemo(
    () => filteredVenues(selectedVenuesValues, searchKeyword),
    [searchKeyword, selectedVenuesValues],
  );

  const handleChange = (venueIds: string[]) => change<keyof FormValues>('venueIds', venueIds);

  const onChangeVenueCheckBox = (id: string, checked: boolean) => {
    let newSelectedVenues = [...venueIds];
    if (checked) {
      newSelectedVenues = [...venueIds, id];
    } else {
      newSelectedVenues = venueIds.filter(venueId => venueId !== id);
    }

    handleChange(newSelectedVenues);
  };

  const onSelectAll = () => {
    const currentVenueIds = selectedVenuesValues.map(item => item.id);
    const matchingVenuesIds = matchingVenues.map(item => item.id).filter(item => !currentVenueIds.includes(item));
    const newVenues = currentVenueIds.concat(...matchingVenuesIds);

    if (newVenues.length) {
      handleChange(newVenues);
    }
  };

  const onDeselectAll = () => {
    const matchingVenuesIds = matchingVenues.map(item => item.id);
    const newVenues = selectedVenuesValues.filter(item => !matchingVenuesIds.includes(item.id)).map(item => item.id);
    handleChange(newVenues);
  };

  const getNumberSearchResults = () => {
    return activeTab === 'all-venues' ? matchingVenues.length : matchingSelectedVenues.length;
  };

  const shouldDisplaySelectAll =
    activeTab === 'all-venues' && matchingVenues.length > 0 && matchingSelectedVenues.length < matchingVenues.length;

  const shouldDisplayDeselectAll =
    activeTab === 'all-venues' &&
    !!matchingSelectedVenues.length &&
    matchingSelectedVenues.length <= matchingVenues.length;

  return (
    <>
      <ModalHeader onCloseClick={onClose}>
        <HeaderContent>
          {onBackClick && (
            <IconButton onClick={onBackClick} variant="lightGrey" size="small">
              <ArrowLeft />
            </IconButton>
          )}
          <FormattedMessage id="venue-groups.form.add-venues-to-group" />
        </HeaderContent>
      </ModalHeader>
      <StyledModalContent>
        <Tabs
          activeTabId={activeTab}
          items={[
            {
              id: 'all-venues',
              label: tabMessage('venue-groups.form.all-venues', totalVenues),
            },
            {
              id: 'selected-venues',
              label: tabMessage('venue-groups.form.selected-venues', venueIds.length),
            },
          ]}
          onChange={id => {
            setActiveTab(id);
            setSearchKeyword('');
          }}
        />

        <SearchContainer>
          <Input
            stretch
            value={searchKeyword}
            onClearClick={() => setSearchKeyword('')}
            onChange={e => setSearchKeyword(e.target.value)}
            placeholder={intl.formatMessage({ id: 'venue-groups.form.search-by-name-address' })}
            icon={<MagnifyingGlass />}
          />
        </SearchContainer>

        <VenueListContainer>
          {submitError && <Error>{submitError}</Error>}

          {activeTab === 'all-venues' && (
            <div data-testid="all-venues">
              {matchingVenues.map(item => (
                <VenueRow
                  key={`${activeTab}-${item.id}`}
                  onCheck={onChangeVenueCheckBox}
                  venue={item}
                  disabled={submitting}
                  checked={venueIds.includes(item.id)}
                />
              ))}
            </div>
          )}

          {activeTab === 'selected-venues' && (
            <div data-testid="selected-venues">
              {matchingSelectedVenues.map(item => (
                <VenueRow
                  key={`${activeTab}-${item.id}`}
                  onRemove={() => onChangeVenueCheckBox(item.id, false)}
                  venue={item}
                />
              ))}
            </div>
          )}

          {getNumberSearchResults() === 0 && (
            <EmptyResults>
              <EmptyImageWrapper>
                <GirlShrugging />
              </EmptyImageWrapper>
              <T.Body3>
                <FormattedMessage id="venue-groups.no-venue" />
              </T.Body3>
            </EmptyResults>
          )}
        </VenueListContainer>
      </StyledModalContent>

      <StyledFooter>
        <LeftPanel>
          {shouldDisplaySelectAll && (
            <TextButton data-testid="select-all" role="button" onClick={onSelectAll} size="small">
              <FormattedMessage id="venue-groups.form.select-all" />
            </TextButton>
          )}

          {shouldDisplayDeselectAll && (
            <TextButton data-testid="deselect-all" role="button" onClick={onDeselectAll} size="small" variant="red">
              <FormattedMessage id="venue-groups.form.deselect-all" />
            </TextButton>
          )}

          {searchKeyword && (
            <NumberSearchResults data-testid="number-search-result">
              <FormattedMessage id="venue-groups.number-search-results" values={{ count: getNumberSearchResults() }} />
            </NumberSearchResults>
          )}
        </LeftPanel>

        <Buttons>
          {displayCancelButton && (
            <Button variant="lightBlue" size="small" onClick={onClose}>
              <FormattedMessage id="state.cancel" />
            </Button>
          )}

          <Button
            type="submit"
            loading={submitting}
            disabled={isDisableSaveButton && disableSaveButtonByDefault}
            variant="blue"
            size="small"
          >
            <FormattedMessage id="state.save" />
          </Button>
        </Buttons>
      </StyledFooter>
    </>
  );
};
